import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from '@mui/joy';
import {
  Plus as AddIcon,
  X as CloseIcon,
  Circle as CircleIcon,
} from '@phosphor-icons/react';
import type { RankingKeyword } from '../../../types/marketing';

interface KeywordSelectorProps {
  keywords: RankingKeyword[];
  selectedKeywordId: number | null;
  onSelect: (id: number) => void;
  onAdd: () => void;
  onDelete: (id: number) => void;
}

export const KeywordSelector: React.FC<KeywordSelectorProps> = ({
  keywords,
  selectedKeywordId,
  onSelect,
  onAdd,
  onDelete,
}) => {
  const maxKeywords = 3;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {keywords.map((kw) => (
        <Chip
          key={kw.id}
          variant={selectedKeywordId === kw.id ? 'solid' : 'outlined'}
          color={selectedKeywordId === kw.id ? 'primary' : 'neutral'}
          size="lg"
          onClick={() => onSelect(kw.id)}
          startDecorator={
            kw.latest_scan?.status === 'pending' || kw.latest_scan?.status === 'in_progress' ? (
              <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />
            ) : (
              <CircleIcon
                size={8}
                color={kw.is_active ? 'var(--joy-palette-success-500)' : 'var(--joy-palette-neutral-400)'}
                weight="fill"
              />
            )
          }
          endDecorator={
            <Tooltip title="Delete keyword">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(kw.id);
                }}
                sx={{
                  '--IconButton-size': '20px',
                  ml: 0.5,
                  color: selectedKeywordId === kw.id ? 'rgba(255,255,255,0.8)' : 'neutral.500',
                  '&:hover': {
                    color: selectedKeywordId === kw.id ? '#fff' : 'danger.500',
                    bgcolor: selectedKeywordId === kw.id ? 'rgba(255,255,255,0.2)' : 'danger.100',
                  },
                }}
              >
                <CloseIcon size={14} />
              </IconButton>
            </Tooltip>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography level="body-sm" fontWeight="lg" sx={{ color: 'inherit' }}>
              {kw.keyword}
            </Typography>
            {kw.last_scanned && (
              <Typography level="body-xs" sx={{ color: 'inherit', opacity: 0.7 }}>
                Last: {new Date(kw.last_scanned).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Chip>
      ))}

      <Button
        variant="outlined"
        color="primary"
        size="sm"
        startDecorator={<AddIcon />}
        onClick={onAdd}
        disabled={keywords.length >= maxKeywords}
      >
        {keywords.length >= maxKeywords ? `${maxKeywords} max` : 'Add Keyword'}
      </Button>
    </Stack>
  );
};
